import { useEffect, useState } from "react";
import { Loader2, Search, ChevronLeft, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function AdminStudents() {
  const [students, setStudents] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/admin/students").then(({ data }) => setStudents(data)); }, []);
  if (!students) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  if (selected) return <StudentDetail userId={selected} onBack={() => setSelected(null)} />;

  const filtered = students.filter((s) => (s.name + s.email).toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <h1 className="font-display text-3xl font-bold uppercase tracking-tight">Students</h1>
      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students..." className="bg-zinc-900 border-white/10 pl-9 text-white" />
      </div>
      <div className="mt-6 overflow-hidden rounded-lg border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-400">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Courses</th><th className="p-3">Joined</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={5} className="p-6 text-center text-zinc-500">No students yet.</td></tr> :
              filtered.map((s) => (
                <tr key={s.id} className="border-t border-white/5" data-testid={`student-row-${s.id}`}>
                  <td className="p-3">{s.name}</td>
                  <td className="p-3 text-zinc-400">{s.email}</td>
                  <td className="p-3">{s.course_count}</td>
                  <td className="p-3 text-zinc-500">{new Date(s.created_at).toLocaleDateString()}</td>
                  <td className="p-3"><Button size="sm" variant="outline" onClick={() => setSelected(s.id)} className="border-white/15 text-white">Manage</Button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StudentDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [grantId, setGrantId] = useState("");

  const load = () => api.get(`/admin/students/${userId}`).then(({ data }) => setData(data));
  useEffect(() => { load(); api.get("/admin/courses").then(({ data }) => setCourses(data)); }, [userId]);
  if (!data) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  const grant = async () => { if (!grantId) return; await api.post(`/admin/students/${userId}/grant`, { course_id: grantId }); toast.success("Access granted"); setGrantId(""); load(); };
  const revoke = async (cid) => { await api.post(`/admin/students/${userId}/revoke`, { course_id: cid }); toast.success("Access revoked"); load(); };

  return (
    <div className="max-w-3xl">
      <button onClick={onBack} className="mb-4 flex items-center gap-1 text-sm text-zinc-400 hover:text-[#DCA846]"><ChevronLeft className="h-4 w-4" /> Back</button>
      <h1 className="font-display text-2xl font-bold">{data.user.name}</h1>
      <p className="text-zinc-400">{data.user.email}</p>

      <h2 className="mt-8 font-display text-lg uppercase tracking-wide">Enrolled Courses</h2>
      <div className="mt-3 space-y-2">
        {data.courses.length === 0 && <p className="text-zinc-500">No course access yet.</p>}
        {data.courses.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded border border-white/10 bg-zinc-900/40 px-4 py-2" data-testid={`student-course-${c.id}`}>
            <span>{c.title}</span>
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400" data-testid={`student-progress-${c.id}`}>
                {c.progress ?? 0}% <span className="text-xs text-zinc-600">({c.completed_lessons ?? 0}/{c.total_lessons ?? 0})</span>
              </span>
              <Button size="sm" variant="ghost" onClick={() => revoke(c.id)} className="text-red-400"><X className="mr-1 h-4 w-4" /> Revoke</Button>
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-8 font-display text-lg uppercase tracking-wide">Grant Access</h2>
      <div className="mt-3 flex gap-2">
        <Select value={grantId} onValueChange={setGrantId}>
          <SelectTrigger className="bg-zinc-900 border-white/10 text-white"><SelectValue placeholder="Select course" /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-white">{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="grant-access-btn" onClick={grant} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Check className="mr-1 h-4 w-4" /> Grant</Button>
      </div>

      <h2 className="mt-8 font-display text-lg uppercase tracking-wide">Orders</h2>
      <div className="mt-3 overflow-hidden rounded-lg border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-left text-xs uppercase text-zinc-400"><tr><th className="p-3">Course</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr></thead>
          <tbody>{data.orders.length === 0 ? <tr><td colSpan={3} className="p-4 text-center text-zinc-500">No orders</td></tr> :
            data.orders.map((o) => <tr key={o.id} className="border-t border-white/5"><td className="p-3">{o.course_title}</td><td className="p-3">${Number(o.amount).toFixed(2)}</td><td className="p-3 capitalize">{o.payment_status}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}
